// src/components/Control/robot/store.ts
// Маленькое хранилище состояния с подпиской по селектору: компонент
// перерисовывается, только когда меняется выбранная им часть (телеметрия
// приходит много раз в секунду — перерисовывать всю страницу нельзя).
import { useCallback, useRef, useSyncExternalStore } from 'react';

export class Store<T> {
  private state: T;
  private listeners = new Set<() => void>();

  constructor(initial: T) {
    this.state = initial;
  }

  get = (): T => this.state;

  set = (patch: Partial<T> | ((s: T) => Partial<T>)) => {
    const p = typeof patch === 'function' ? patch(this.state) : patch;
    this.state = { ...this.state, ...p };
    this.listeners.forEach(l => l());
  };

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
}

export function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a as object);
  const kb = Object.keys(b as object);
  if (ka.length !== kb.length) return false;
  return ka.every(k => Object.is((a as any)[k], (b as any)[k]));
}

export function useStore<T, S>(store: Store<T>, selector: (s: T) => S, isEqual: (a: S, b: S) => boolean = shallowEqual): S {
  const cache = useRef<{ value: S } | null>(null);
  const selectorRef = useRef(selector);
  selectorRef.current = selector;
  const getSnapshot = useCallback(() => {
    const next = selectorRef.current(store.get());
    if (cache.current && isEqual(cache.current.value, next)) return cache.current.value;
    cache.current = { value: next };
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);
  return useSyncExternalStore(store.subscribe, getSnapshot);
}
